import { describeExportFilters } from '../../utils/dataExport';
import { gridExportColumns, loadedGridRows, serverGridOrder, sortedColumnStates, type ColumnExportHints } from '../../utils/gridExportColumns';
import { ExportButton, type ExportConfiguration } from '../DataExport/ExportButton';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Button, InputGroup, NonIdealState } from '@blueprintjs/core';
import type {
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  ColDef,
  ColumnState,
  GridReadyEvent,
  IServerSideDatasource,
  RowClickedEvent,
  CellMouseOverEvent,
  GetMainMenuItemsParams,
} from 'ag-grid-community';
import { toast as message } from 'sonner';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './ReportsIndex.css';
import type { ProcessedReportInfo, ReportField } from '../../types/reporting.js';
import type { ProcessedMetadata } from '../../utils/metadata/main-metadata.js';
import { queryClient } from '../../utils/constants.js';
import { formatLongDateTime } from '../../utils/dateFormat.js';
import { isUserColumnEvent } from '../../utils/columnState.js';
import { updateRecord } from '../../utils/supabase-operations.js';
import { invalidateAndRefreshAllQueriesForTable } from '../../utils/metadata/cache.js';
import { useEditLocks } from '../../hooks/useEditLocks.js';
import { useFillGridHeight } from '../../hooks/useFillGridHeight.js';
import { useTableZoom } from '../../utils/tableViewSettings.js';
import { GridZoomControl } from '../ui/GridZoomControl.js';
import { ResetColumnsButton } from '../ui/ResetColumnsButton.js';
import { SaveDefaultLayoutButton } from '../ui/SaveDefaultLayoutButton.js';
import { reconcileSavedLayout, type GridDefaultsSnapshot } from '../../utils/gridColumnDefaults.js';
import { supabaseClient } from '../../utils/supabase.js';
import { ResetFiltersButton } from '../ui/ResetFiltersButton.js';
import { ActiveFilterChips } from '../ui/ActiveFilterChips.js';
import { useActiveColumnFilters } from '../ui/useActiveColumnFilters.js';
import { MaterializedReportStatus, reportMaterializedName } from '../ui/MaterializedReportStatus.js';
import { resetColumnLayout } from '../../utils/resetColumnLayout.js';
import { resetGridFilters } from '../../utils/resetGridFilters.js';
import { useMyCapabilities } from '../../utils/metadata/capabilities.js';
import { formatSubmissionTerm } from '../../utils/submissionTerm.js';
import { missingDocumentsItems, missingDocumentsList } from '../../utils/missingDocuments.js';
import { formatLooseCurrency } from '../../utils/looseCurrency.js';
import { useQuery } from '@tanstack/react-query';
import {
  createReportDataSource,
  fetchReportColumnStat,
  fetchReportRowsFromSupabase,
  hasIndexedSorts,
  isIndexedSort,
  pgTypeToAgType,
  reportOrderTerms,
  UNINDEXED_SORT_MAX_ROWS,
  reportColumnStatPlan,
  reportColumnStatQueryKey,
  type ReportCountMode,
} from './reportGridDataSource.js';
import { columnMenuWithStat } from '../ui/ColumnStatMenuItem.js';
import { REPORT_ROWS_QUERY_KEY } from './reportGridDataSource.js';
import { ReportParameterBar } from './ReportParameterBar.js';
import RecordLink from '../links/RecordLink.js';
import { LivePicklistCell, type LivePicklistTarget } from './LivePicklistCell.js';
import { EditModeToggle, UndoLastEditButton } from '../TableGrid/EditModeToggle.js';
import type { GridFilterOptionRequest } from '../../utils/gridFilterOptions.js';
import { typedFilterWithCheckboxes } from '../TableGrid/gridCheckboxFilters.js';
import {
  GridRowActions,
  isGridInteractiveTarget,
  type GridRowAction,
} from '../GridRowActions/GridRowActions.js';
import {
  REPORT_GRID_PAGE_SIZE,
  paramValuesForReport,
  type ReportSortModelItem,
} from './reportGridState.js';
import { useHoverPrefetch } from '../../hooks/useHoverPrefetch.js';
import { useGridPopupCleanup } from '../../hooks/useGridPopupCleanup.js';
import { onReportGridRefresh } from '../../utils/reportGridRefresh.js';
import { searchGridFetcher } from '../../utils/searchApiGrid';


export {
  REPORT_GRID_PAGE_SIZE,
  decodeGridState,
  decodeSortState,
  encodeGridState,
  encodeSortState,
  paramValuesForReport,
  type ReportSortModelItem,
} from './reportGridState.js';

export interface ReportGridUrlState {
  filterModel: Record<string, any>;
  sortModel: ReportSortModelItem[];
  onFilterModelChange?: (filterModel: Record<string, any>) => void;
  onSortModelChange?: (sortModel: ReportSortModelItem[]) => void;
}

/**
 * Per-column aggregate for the filtered-totals row (REG-650). Keyed by the
 * visible column's field name; configured in the dashboard component JSON as
 * `data.summary` and rendered as a pinned row above the data whenever any
 * filter is active.
 */
export interface ReportSummarySpec {
  kind: 'date_range' | 'mode' | 'count' | 'pct' | 'sum' | 'avg';
  /** For `pct`: the value whose share is reported (e.g. "Good"). */
  value?: string;
  /** For `count`: unit label appended to the number (e.g. "units"). */
  label?: string;
  /** Aggregate a different column than the one displayed under (e.g. term_months). */
  source?: string;
  digits?: number;
  money?: boolean;
  percent?: boolean;
  suffix?: string;
}

const SUMMARY_ROW_CAP = 5000;

function compactMoney(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(Math.abs(value) >= 10_000_000 ? 0 : 2)}M`;
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${Math.round(value).toLocaleString('en-US')}`;
}

function summaryShortDate(value: unknown): string {
  if (!value) return '';
  const date = new Date(String(value));
  return Number.isNaN(date.getTime())
    ? ''
    : new Intl.DateTimeFormat('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }).format(date);
}

/** Columns the summary fetch needs: aggregate sources plus label fields for modes. */
export function summaryFetchColumns(
  summary: Record<string, ReportSummarySpec>,
  report: ProcessedReportInfo,
): string[] {
  const fieldNamesSet = new Set(report.fields.map((f) => f.name));
  const columns = new Set<string>();
  Object.entries(summary).forEach(([field, spec]) => {
    if (spec.kind === 'count') return;
    const source = spec.source ?? field;
    if (spec.kind === 'mode' && fieldNamesSet.has(`${source}_label`)) {
      columns.add(`${source}_label`);
      return;
    }
    if (fieldNamesSet.has(source)) columns.add(source);
  });
  return [...columns];
}

function computeSummaryRow(
  rows: Record<string, any>[],
  summary: Record<string, ReportSummarySpec>,
  report: ProcessedReportInfo,
): Record<string, string> {
  const fieldNamesSet = new Set(report.fields.map((f) => f.name));
  const result: Record<string, string> = {};
  Object.entries(summary).forEach(([field, spec]) => {
    const source = spec.source ?? field;
    switch (spec.kind) {
      case 'count': {
        result[field] = `${rows.length.toLocaleString('en-US')}${spec.label ? ` ${spec.label}` : ''}`;
        break;
      }
      case 'date_range': {
        const times = rows
          .map((row) => (row[source] ? new Date(row[source]).getTime() : NaN))
          .filter((time) => Number.isFinite(time));
        if (times.length === 0) { result[field] = '—'; break; }
        const min = summaryShortDate(new Date(Math.min(...times)).toISOString());
        const max = summaryShortDate(new Date(Math.max(...times)).toISOString());
        result[field] = min === max ? min : `${min} – ${max}`;
        break;
      }
      case 'mode': {
        const labelColumn = fieldNamesSet.has(`${source}_label`) ? `${source}_label` : source;
        const counts = new Map<string, number>();
        rows.forEach((row) => {
          const value = row[labelColumn];
          if (value == null || value === '') return;
          const key = String(value);
          counts.set(key, (counts.get(key) ?? 0) + 1);
        });
        const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
        result[field] = top ? `${top[0]} · ${top[1]}` : '—';
        break;
      }
      case 'pct': {
        const values = rows
          .map((row) => row[source])
          .filter((value) => value != null && String(value).trim() !== '');
        if (values.length === 0) { result[field] = '—'; break; }
        const matches = values.filter((value) => String(value) === spec.value).length;
        result[field] = `${Math.round((matches / values.length) * 100)}% ${spec.value ?? ''}`.trim();
        break;
      }
      case 'sum':
      case 'avg': {
        const numbers = rows
          .map((row) => Number(row[source]))
          .filter((value) => Number.isFinite(value));
        if (numbers.length === 0) { result[field] = '—'; break; }
        const total = numbers.reduce((sum, value) => sum + value, 0);
        const value = spec.kind === 'sum' ? total : total / numbers.length;
        if (spec.money) result[field] = compactMoney(value);
        else if (spec.percent) result[field] = `${value.toFixed(spec.digits ?? 1)}%`;
        else result[field] = `${value.toFixed(spec.digits ?? 0)}${spec.suffix ?? ''}`;
        break;
      }
    }
  });
  return result;
}

export function reportSummaryQueryOptions({
  summary,
  report,
  paramValues,
  baseFilterModel,
  filterModel,
}: {
  summary: Record<string, ReportSummarySpec>;
  report: ProcessedReportInfo;
  paramValues: Record<string, string>;
  baseFilterModel: Record<string, any>;
  filterModel: Record<string, any>;
}) {
  const fields = summaryFetchColumns(summary, report);
  return {
    queryKey: [
      REPORT_ROWS_QUERY_KEY, 'summary-row', report.report_key, report.updated_at,
      paramValues, baseFilterModel, filterModel, fields,
    ] as const,
    staleTime: 30_000,
    queryFn: async () => {
      const { rows } = await fetchReportRowsFromSupabase({
        report,
        paramValues,
        filterModel: { ...baseFilterModel, ...filterModel },
        sortModel: [],
        startRow: 0,
        endRow: SUMMARY_ROW_CAP,
        countMode: 'none',
        fields,
      });
      return computeSummaryRow(rows, summary, report);
    },
  };
}

/**
 * The pinned summary row carries preformatted strings, so every column's own
 * formatter/renderer must step aside for it.
 */
function withSummaryRowSupport(cols: ColDef[]): ColDef[] {
  return cols.map((col) => {
    const { valueFormatter, cellRenderer, editable } = col;
    return {
      ...col,
      valueFormatter: (params: any) => {
        if (params.node?.rowPinned === 'top') return params.value ?? '';
        return typeof valueFormatter === 'function' ? valueFormatter(params) : params.value;
      },
      cellRendererSelector: (params: any) => {
        if (params.node?.rowPinned === 'top') {
          return { component: (p: any) => <span className="report-summary-cell">{p.value ?? ''}</span> };
        }
        return cellRenderer ? { component: cellRenderer } : undefined;
      },
      cellRenderer: undefined,
      editable: (params: any) => {
        if (params.node?.rowPinned === 'top') return false;
        return typeof editable === 'function' ? (editable as any)(params) : Boolean(editable);
      },
    };
  });
}

interface ReportGridProps {
  report: ProcessedReportInfo;
  metadata: ProcessedMetadata;
  navigateToRecord?: (tableName: string, recordId: number | string, label?: string) => void;
  onRowClick?: (row: Record<string, any>) => void;
  prefetchRowRecord?: (row: Record<string, any>) => Promise<void>;
  rowActions?: GridRowAction<Record<string, any>>[];
  showHeader?: boolean;
  showDescription?: boolean;
  showParameters?: boolean;
  className?: string;
  height?: number | string;
  /**
   * Treat a numeric `height` as a minimum and grow the grid to the bottom of
   * the visible content area, so zooming the browser out (or a taller window)
   * shows more rows instead of leaving dead space (REG-652).
   */
  fillHeight?: boolean;
  rowHeight?: number;
  headerHeight?: number;
  fitColumns?: boolean;
  showViewRecord?: boolean;
  initialParamValues?: Record<string, string>;
  baseFilterModel?: Record<string, any>;
  onParamValuesApply?: (values: Record<string, string>) => void;
  onEditReport?: () => void;
  contextBadge?: string;
  /** Extra controls rendered in the header actions area (before search). */
  headerActions?: React.ReactNode;
  urlState?: ReportGridUrlState;
  /** Sort configured by the page/report, reapplied when personal column state is reset. */
  resetSortModel?: ReportSortModelItem[];
  initialColumnState?: ColumnState[];
  /** REG-828: the report's default columns the saved state was made against. */
  initialColumnDefaults?: GridDefaultsSnapshot | null;
  /** The user's column layout, with the report default it was made against; [] after a reset. */
  onColumnStateChange?: (columnState: ColumnState[], defaults?: GridDefaultsSnapshot) => void;
  countMode?: ReportCountMode;
  dashboardKey?: string;
  datasetKey?: string;
  /** Filtered-totals pinned row config (REG-650); shown while filters are active. */
  summary?: Record<string, ReportSummarySpec>;
  /**
   * REG-828: tick rows for a bulk action. Adds a pinned checkbox column
   * (colId `bulk-selection`); rows are identified by their `id`. The header box
   * ticks the loaded rows.
   */
  rowSelection?: 'multiple';
  /** The ticked rows, after every selection change. */
  onSelectionChanged?: (rows: Record<string, any>[]) => void;
  /** Increment to clear the selection from the parent. */
  selectionResetKey?: number;
  /**
   * The filter the search box applies, in place of the default full-text
   * search on `full_text_search` (for example a number search on an id
   * column). Returning null applies no filter.
   */
  searchFilterModel?: (query: string) => Record<string, any> | null;
  /** Search box placeholder. */
  searchPlaceholder?: string;
  /** Overlay text when no row matches. */
  noRowsMessage?: string;
  /**
   * REG-739 / REG-828: an inline edit was saved. The page records it for Undo
   * (useEditUndoStack); `undo` then renders the Undo button by the Edit toggle.
   */
  onCellEditSaved?: (edit: ReportCellEdit) => void;
  undo?: { canUndo: boolean; nextUndoLabel: string | null; onUndo: () => void };
}

/** One saved inline edit on a report grid's primary table. */
export interface ReportCellEdit {
  tableName: string;
  schema: string;
  recordId: number | string;
  field: string;
  oldValue: unknown;
  newValue: unknown;
  label: string;
  dataType?: string | null;
}

/** REG-828: the checkbox column of a report grid with row selection. */
const BULK_SELECTION_COLUMN: ColDef = {
  headerName: '',
  colId: 'bulk-selection',
  width: 46,
  minWidth: 46,
  maxWidth: 46,
  checkboxSelection: true,
  headerCheckboxSelection: true,
  sortable: false,
  filter: false,
  resizable: false,
  suppressMenu: true,
  pinned: 'left',
};

const EMPTY_REPORT_FILTER_MODEL: Record<string, any> = {};
const EMPTY_REPORT_SORT_MODEL: ReportSortModelItem[] = [];
const EMPTY_REPORT_ROW_ACTIONS: GridRowAction<Record<string, any>>[] = [];

function stableJson(value: unknown): string {
  return JSON.stringify(value ?? null);
}


function sortModelFromGridApi(api: GridReadyEvent['api']): ReportSortModelItem[] {
  return sortedColumnStates(api)
    .map((col, index) => ({
      colId: col.colId!,
      sort: col.sort as 'asc' | 'desc',
      sortIndex: col.sortIndex ?? index,
    }));
}

function applySortModel(api: GridReadyEvent['api'], sortModel: ReportSortModelItem[]): void {
  const sortedByColId = new Map(sortModel.map((item, index) => [item.colId, { ...item, sortIndex: item.sortIndex ?? index }]));
  const nextState = api.getColumnState().map((col) => {
    const sort = sortedByColId.get(col.colId);
    return {
      colId: col.colId,
      sort: sort?.sort ?? null,
      sortIndex: sort?.sortIndex ?? null,
    };
  });

  api.applyColumnState({ state: nextState });
}

function cleanedColumnState(api: GridReadyEvent['api']): ColumnState[] {
  return api.getColumnState().map((col) => ({
    colId: col.colId,
    hide: col.hide,
    width: col.width,
    pinned: col.pinned,
    sort: col.sort,
    sortIndex: col.sortIndex,
    aggFunc: col.aggFunc,
    rowGroup: col.rowGroup,
    rowGroupIndex: col.rowGroupIndex,
    pivot: col.pivot,
    pivotIndex: col.pivotIndex,
    flex: col.flex,
  }));
}

function applySavedColumnState(api: GridReadyEvent['api'], columnState: ColumnState[] | undefined): void {
  if (!columnState || columnState.length === 0) return;
  const existing = new Set(api.getColumnState().map((col) => col.colId));
  const valid = columnState.filter((col) => col.colId && existing.has(col.colId));
  if (valid.length === 0) return;
  api.applyColumnState({ state: valid, applyOrder: true });
}

/**
 * REG-874: what the Export dialog needs for one report column: the extra row
 * fields its formatter reads, its Excel number format, and its record link.
 */
function reportColumnExport(
  f: ReportField,
  uiData: Record<string, unknown>,
  report: ProcessedReportInfo,
  { labelField, targetTable, typed, agType }: { labelField: string | null; targetTable: string | null; typed: boolean; agType: string },
): ColumnExportHints {
  const recordIdField = typeof uiData.record_id_field === 'string' ? uiData.record_id_field : 'id';
  const recordTable = typeof uiData.record_table === 'string' ? uiData.record_table : report.primary_table_name;
  const structureField = typeof uiData.structure_field === 'string' ? uiData.structure_field : 'structure';
  const fields = [
    ...(labelField ? [labelField] : []),
    ...(uiData.display === 'submission_term' ? [structureField] : []),
  ];
  const link = labelField && targetTable ? { table: targetTable }
    : uiData.record_link === true && recordTable ? { table: recordTable, idField: recordIdField } : undefined;
  // Display variants (status pills, long dates, loose amounts, terms) are text in the grid too.
  const excel: ColumnExportHints['excel'] = !typed || typeof uiData.display === 'string' ? undefined
    // Same as the cell: Intl currency with at least ui_precision decimals and up to two.
    : f.ui_is_currency ? { kind: 'currency', precision: f.ui_precision ?? 2, maxPrecision: Math.max(f.ui_precision ?? 2, 2) }
    : f.ui_is_percentage ? { kind: 'percent', precision: f.ui_precision ?? 2 }
    : agType === 'number' && isIntegerType(f.data_type) ? { kind: 'number', precision: 0 }
    : agType === 'number' && f.ui_precision != null ? { kind: 'decimal', precision: f.ui_precision }
    : f.data_type === 'date' ? { kind: 'date' }
    : agType === 'date' ? { kind: 'timestamp_date' }
    : undefined;
  return { fields, ...(excel ? { excel } : {}), ...(link ? { link } : {}) };
}

function isIntegerType(dataType: string): boolean {
  return dataType.includes('int');
}

function safeColumnSize(value: unknown, min: number, max: number): number | undefined {
  if (value == null || value === '') return undefined;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.min(Math.max(numeric, min), max) : undefined;
}

function relationshipTargetTable(fieldName: string, report: ProcessedReportInfo, metadata: ProcessedMetadata): string | null {
  const primaryTableRelationship = report.primary_table_name
    ? metadata.relationshipLookupData?.[report.primary_table_name]?.byFieldName?.[fieldName]
    : null;

  if (primaryTableRelationship?.target_table_name) {
    return primaryTableRelationship.target_table_name;
  }

  if (metadata.tableData?.byName?.[fieldName]) {
    return fieldName;
  }

  return null;
}

/**
 * Config-level editability for a report column, before edit mode and per-row
 * locks are considered.
 *
 * A column opts in through its report_field `ui_data`:
 *   editable        must be true
 *   edit_field      base-table column to write (defaults to the report field name)
 *   edit_capability optional core_crm capability the user must hold
 *
 * The capability check is UX only — `deal_rls.sql` enforces `edit_deals` (and
 * the commission guard enforces `edit_commissions`) on the underlying tables, so
 * a user without the capability cannot write even if the grid let them try.
 */
function isReportColumnEditable(
  field: ReportField,
  baseField: any,
  hasCapability: (key: string) => boolean,
): boolean {
  const uiData = typeof field.ui_data === 'object' && field.ui_data !== null ? field.ui_data : {};
  if (uiData.editable !== true || !baseField) return false;
  if (baseField.is_system || baseField.is_calculated) return false;
  const capability = typeof uiData.edit_capability === 'string' ? uiData.edit_capability : null;
  return !capability || hasCapability(capability);
}

/**
 * agRichSelectCellEditor returns false from isPopup(), so its option list is
 * laid out inside the cell. These grids run at rowHeight 41 with a 300px list,
 * which meant an open dropdown painted over every row beneath it. Rendering the
 * editor in a popup layer keeps the rows visible behind the list.
 */
const RICH_SELECT_POPUP = {
  cellEditorPopup: true,
  cellEditorPopupPosition: 'under',
} as const;

/**
 * Editor wiring for an inline-editable report column.
 *
 * Report grids are server-side and carry only the report view's columns, so the
 * relationship editors used by TableGrid (which rely on a `<field>_rel` payload)
 * do not apply. A relationship column is therefore editable only when its target
 * table is one of the client-side reference tables already cached in metadata;
 * anything larger stays read-only rather than silently offering a broken editor.
 *
 * Returns null when the column cannot be edited safely.
 */
function reportCellEditorConfig(
  baseField: any,
  metadata: ProcessedMetadata,
): Partial<ColDef> | null {
  const relationship = baseField?.relationship;
  if (relationship?.target_table_name) {
    const targetTable = relationship.target_table_name as string;
    const items = (metadata.clientSideSelectTables as any)?.[targetTable];
    if (!Array.isArray(items)) return null;
    const sorted = items.slice().sort((a: any, b: any) => String(a.label).localeCompare(String(b.label)));
    return {
      cellEditor: 'agRichSelectCellEditor',
      ...RICH_SELECT_POPUP,
      cellEditorParams: {
        values: [...sorted.map((item: any) => item.id), null],
        formatValue: (value: any) => {
          if (value === null || value === undefined) return 'Clear';
          const cached = (metadata.clientSideSelectTables as any)?.flattened?.[`${targetTable}_${value}`];
          return cached?.label ?? String(value);
        },
        valueListMaxHeight: 300,
        valueListMaxWidth: 250,
        searchType: 'matchAny',
        highlightMatch: true,
      },
    };
  }

  const picklistValues = baseField?.picklist?.values;
  if (Array.isArray(picklistValues) && picklistValues.length > 0) {
    return {
      cellEditor: 'agRichSelectCellEditor',
      ...RICH_SELECT_POPUP,
      cellEditorParams: {
        values: [...picklistValues.filter((item: any) => !!item?.value).map((item: any) => item.value), null],
        formatValue: (value: any) => (value === null || value === undefined ? 'Clear' : String(value)),
        valueListMaxHeight: 300,
        valueListMaxWidth: 250,
        searchType: 'matchAny',
        highlightMatch: true,
      },
    };
  }

  switch (baseField?.data_type) {
    case 'date':
    case 'timestamp without time zone':
    case 'timestamp with time zone':
      return { cellEditor: 'agDateCellEditor' };
    case 'numeric':
    case 'integer':
    case 'bigint':
      return {
        cellEditor: 'agNumberCellEditor',
        valueParser: (params: any) => {
          const parsed = parseFloat(params.newValue);
          return Number.isNaN(parsed) ? null : parsed;
        },
      };
    case 'boolean':
      return { cellEditor: 'agCheckboxCellEditor' };
    default:
      return {};
  }
}

function buildColDefs(
  report: ProcessedReportInfo,
  metadata: ProcessedMetadata,
  paramValues: Record<string, unknown>,
  navigateToRecord?: (tableName: string, recordId: number | string, label?: string) => void,
  showViewRecord = true,
  rowActions: GridRowAction<Record<string, any>>[] = [],
  editMode = false,
  getLockOwner?: (rowId: string | number, colId?: string | null) => string | null,
  hasCapability: (key: string) => boolean = () => false,
): ColDef[] {
  // REG-670: related columns are registered hidden so the grid does not request
  // them, but they still have to exist as columns or the column chooser has
  // nothing to offer — which is why the first attempt showed nothing. They are
  // built here and start hidden.
  const visibleFields = report.fields
    .filter((f) => f.is_visible || f.name.endsWith('_ref'))
    .sort((a, b) => a.ordinal_position - b.ordinal_position);
  const fieldNames = new Set(report.fields.map((f) => f.name));

  const cols: ColDef[] = visibleFields.map((f: ReportField) => {
    const label = f.ui_label ?? f.name;
    const agType = pgTypeToAgType(f.data_type);
    const uiData = typeof f.ui_data === 'object' && f.ui_data !== null ? f.ui_data : {};
    const editField = typeof uiData.edit_field === 'string' ? uiData.edit_field : f.name;
    const baseField = report.primary_table_name
      ? metadata.fieldData?.[report.primary_table_name]?.byFieldName?.[editField]
      : null;
    const prefix = typeof uiData.prefix === 'string' ? uiData.prefix : '';
    const suffix = typeof uiData.suffix === 'string' ? uiData.suffix : '';
    const decorate = (value: string): string => value ? `${prefix}${value}${suffix}` : value;
    const labelField = `${f.name}_label`;
    const hasLabelField = fieldNames.has(labelField);
    const targetTable = hasLabelField ? relationshipTargetTable(f.name, report, metadata) : null;
    // REG-740: an always-live dropdown (ui_data.edit_always) is its own cell,
    // outside Edit mode; it may write to another table through an RPC.
    const liveTarget: LivePicklistTarget | null = uiData.editable === true && uiData.edit_always === true
      ? {
        table: typeof uiData.edit_table === 'string' ? uiData.edit_table : (report.primary_table_name ?? ''),
        field: editField,
        capability: typeof uiData.edit_capability === 'string' ? uiData.edit_capability : null,
        rpc: typeof uiData.edit_rpc === 'string' ? uiData.edit_rpc : null,
        idParam: typeof uiData.edit_rpc_id_param === 'string' ? uiData.edit_rpc_id_param : 'p_id',
      }
      : null;
    const liveIdField = typeof uiData.edit_rpc_id_field === 'string' ? uiData.edit_rpc_id_field : 'id';
    const editorConfig = !liveTarget && isReportColumnEditable(f, baseField, hasCapability)
      ? reportCellEditorConfig(baseField, metadata)
      : null;
    const suppressRecordLink = editMode && Boolean(editorConfig);
    // REG-921: a lookup column (an id with a `<name>_label` partner) shows and
    // filters by its name. applyReportFilters sends typed filters to the label
    // column, so the typed filter is a text filter, never a number filter on the id.
    const typedFilter = hasLabelField
      ? 'agTextColumnFilter'
      : agType === 'number'
      ? 'agNumberColumnFilter'
      : agType === 'date'
        ? 'agDateColumnFilter'
        : 'agTextColumnFilter';
    const optionRequest: GridFilterOptionRequest = {
      sourceSchema: report.source_schema,
      sourceName: report.source_name,
      columnName: f.name,
      labelColumnName: hasLabelField ? labelField : undefined,
      reportKey: report.report_key,
      paramValues,
    };

    const col: ColDef = {
      field: f.name,
      headerName: label,
      // REG-670: related columns exist so they can be chosen, but start hidden.
      ...(f.is_visible ? {} : { hide: true }),
      ...typedFilterWithCheckboxes(typedFilter, { buttons: ['clear'] }, { optionRequest }),
      sortable: true,
      resizable: true,
      minWidth: 100,
      ...(editorConfig ?? {}),
      editable: (params) => Boolean(editorConfig) && editMode
        && !getLockOwner?.(params.data?.id, params.column?.getColId()),
      context: {
        editField,
        dataType: baseField?.data_type,
        editable: Boolean(editorConfig),
      },
      cellClassRules: {
        'live-cell-editing': (params) =>
          Boolean(getLockOwner?.(params.data?.id, params.column?.getColId())),
      },
      tooltipValueGetter: (params) => {
        const owner = getLockOwner?.(params.data?.id, f.name);
        return owner ? `Currently being edited by ${owner}` : null;
      },
    };
    col.width = safeColumnSize(uiData.width, 46, 800);
    col.minWidth = safeColumnSize(uiData.min_width, 46, 800) ?? col.minWidth;
    col.maxWidth = safeColumnSize(uiData.max_width, 46, 1200);
    col.flex = safeColumnSize(uiData.flex, 0, 12);

    if (liveTarget) {
      col.cellRenderer = (p: any) => (
        <LivePicklistCell
          target={liveTarget}
          recordId={p.data?.[liveIdField]}
          value={p.value ?? null}
          label={label}
        />
      );
      col.cellClass = 'live-picklist-cell';
    } else if (hasLabelField) {
      col.valueFormatter = (p) => p.data?.[labelField] ?? '';
      col.cellRenderer = (p: any) => {
        const displayValue = p.data?.[labelField] ?? '';
        if (!p.value || !targetTable) return <span>{displayValue}</span>;
        // While the column is being edited the whole cell must be a click
        // target for the editor. A record link covering the text would
        // navigate away instead, forcing people to aim at the few pixels
        // around it.
        if (suppressRecordLink) return <span>{displayValue}</span>;
        if (uiData.link_style === 'text' && navigateToRecord) {
          return (
            <button
              type="button"
              className="report-record-text-link"
              onClick={() => navigateToRecord(targetTable, p.value,
                typeof displayValue === 'string' && displayValue.trim() ? displayValue : undefined)}
            >
              {displayValue}
            </button>
          );
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <RecordLink tableName={targetTable} recordId={p.value} className="record-link-in-cell" />
            <span>{displayValue}</span>
          </div>
        );
      };
    } else if (uiData.display === 'status_pill') {
      col.cellRenderer = (p: any) => {
        if (!p.value) return null;
        const statusClass = String(p.value).toLowerCase().replace(/[^a-z0-9]+/g, '-');
        return (
          <span className={`report-status-pill report-status-pill--${statusClass}`}>
            <i />
            {String(p.value)}
          </span>
        );
      };
    } else if (uiData.record_link === true && (report.primary_table_name || uiData.record_table) && navigateToRecord) {
      const recordTable = typeof uiData.record_table === 'string' ? uiData.record_table : report.primary_table_name!;
      const recordIdField = typeof uiData.record_id_field === 'string' ? uiData.record_id_field : 'id';
      col.valueFormatter = (p) => (p.value != null ? decorate(String(p.value)) : '');
      col.cellRenderer = (p: any) => {
        if (p.data?.[recordIdField] == null || p.value === null || p.value === undefined) return null;
        if (suppressRecordLink) return <span>{decorate(String(p.value))}</span>;
        return (
          <button
            type="button"
            className="report-primary-record-link"
            onClick={() => navigateToRecord(recordTable, p.data[recordIdField])}
          >
            {decorate(String(p.value))}
          </button>
        );
      };
    } else if (uiData.display === 'doc_list') {
      // A JSON-encoded string array (Multi Select picklist value, e.g.
      // submission.missing_documents) rendered as one amber bubble per
      // document, matching the merchant side panel's missing-doc pill.
      // With ui_data.link_field set (e.g. "submission"), clicking a bubble
      // opens that related record — the row click keeps its own target.
      // link_field "id" opens the row's own record (the report's primary table).
      const docLinkField = typeof uiData.link_field === 'string' ? uiData.link_field : null;
      const docLinkTable = docLinkField === 'id'
        ? report.primary_table_name ?? null
        : docLinkField ? relationshipTargetTable(docLinkField, report, metadata) : null;
      col.valueFormatter = (p) => missingDocumentsList(p.value);
      col.tooltipValueGetter = (p) => missingDocumentsList(p.data?.[f.name]) || null;
      col.cellRenderer = (p: any) => {
        const docs = missingDocumentsItems(p.value);
        if (docs.length === 0) return null;
        const linkId = docLinkField ? p.data?.[docLinkField] : null;
        const linkLabel = docLinkField && typeof p.data?.[`${docLinkField}_label`] === 'string'
          ? p.data[`${docLinkField}_label`]
          : undefined;
        const openLinked = docLinkTable && navigateToRecord && linkId != null
          ? () => navigateToRecord(docLinkTable, linkId, linkLabel)
          : null;
        return (
          <span className="report-doc-pills">
            {docs.map((doc) => (openLinked ? (
              <button key={doc} type="button" className="report-doc-pill report-doc-pill--link" onClick={openLinked}>
                {doc}
              </button>
            ) : (
              <span key={doc} className="report-doc-pill">{doc}</span>
            )))}
          </span>
        );
      };
    } else if (uiData.display === 'loose_currency') {
      // Free-text amount fields (merchant.requesting): a value that is only
      // digits/commas/$ renders as rounded currency ("150,000" → "$150,000");
      // anything else ("20k", "Maximum", "Unsure") stays as typed.
      col.valueFormatter = (p) => formatLooseCurrency(p.value);
    } else if (f.ui_is_currency) {
      col.valueFormatter = (p) =>
        p.value != null ? decorate(new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: f.ui_precision ?? 2 }).format(Number(p.value))) : '';
    } else if (f.ui_is_percentage) {
      col.valueFormatter = (p) =>
        p.value != null ? decorate(`${Number(p.value).toFixed(f.ui_precision ?? 2)}%`) : '';
    } else if (uiData.display === 'short_datetime') {
      // REG-953: "Oct 30, 9:11 PM" (New York time), date and time in a narrow column.
      col.valueFormatter = (p) => (p.value ? decorate(new Date(p.value).toLocaleString('en-US', {
        timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
      })) : '');
    } else if (uiData.display === 'long_datetime') {
      // REG-828: "Thursday, September 10, 2026 at 9:11:45 PM" (New York time).
      col.valueFormatter = (p) => (p.value ? decorate(formatLongDateTime(p.value)) : '');
    } else if (uiData.display === 'plain') {
      // Identifiers (Merchant ID) read as typed, without a thousands separator.
      col.valueFormatter = (p) => (p.value != null ? decorate(String(p.value)) : '');
    } else if (uiData.display === 'submission_term') {
      const structureField = typeof uiData.structure_field === 'string'
        ? uiData.structure_field
        : 'structure';
      col.valueFormatter = (p) => formatSubmissionTerm(p.value, p.data?.[structureField]);
    } else if (agType === 'number' && isIntegerType(f.data_type)) {
      col.valueFormatter = (p) =>
        p.value != null ? decorate(new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(p.value))) : '';
    } else if (agType === 'number' && f.ui_precision != null) {
      col.valueFormatter = (p) =>
        p.value != null ? decorate(Number(p.value).toFixed(f.ui_precision!)) : '';
    } else if (f.data_type === 'date') {
      // A SQL date is a calendar day, not UTC midnight in the viewer's zone.
      col.valueFormatter = (p) => p.value ? decorate(new Date(`${p.value}T12:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC' })) : '';
    } else if (agType === 'date') {
      col.valueFormatter = (p) =>
        p.value ? decorate(new Date(p.value).toLocaleDateString()) : '';
    } else if (prefix || suffix) {
      col.valueFormatter = (p) => p.value != null ? decorate(String(p.value)) : '';
    }

    col.context = { ...col.context, export: reportColumnExport(f, uiData, report, {
      labelField: hasLabelField ? labelField : null,
      targetTable: liveTarget ? null : targetTable,
      typed: !liveTarget && !hasLabelField && !prefix && !suffix,
      agType,
    }) };
    return col;
  });

  if (showViewRecord && report.primary_table_name && navigateToRecord) {
    cols.push({
      headerName: '',
      field: '__view_record',
      width: 100,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (params: any) => {
        if (!params.data?.id) return null;
        return (
          <Button
            small
            minimal
            onClick={() => navigateToRecord(report.primary_table_name!, params.data.id)}
          >
            View
          </Button>
        );
      },
    });
  }

  const leftRowActions = rowActions.filter((action) => action.side !== 'right');
  const rightRowActions = rowActions.filter((action) => action.side === 'right');

  if (leftRowActions.length > 0) {
    // Labeled button actions (variant 'button') need more room than the 27px icon squares.
    const actionsWidth = 46 + leftRowActions.filter((a) => a.variant === 'button').reduce((sum, a) => sum + (a.buttonWidth ?? 52), 0);
    cols.unshift({
      headerName: '',
      colId: '__row_actions',
      width: actionsWidth,
      minWidth: actionsWidth,
      maxWidth: actionsWidth,
      sortable: false,
      filter: false,
      resizable: false,
      pinned: 'left',
      suppressMenu: true,
      cellRenderer: ({ data }: { data?: Record<string, any> }) => (
        data ? <GridRowActions row={data} actions={leftRowActions} /> : null
      ),
    });
  }

  if (rightRowActions.length > 0) {
    // Pinned right so these stay in view even when the detail side panel
    // narrows the grid (REG-654).
    const actionsWidth = 42 + rightRowActions.filter((a) => a.variant === 'button').reduce((sum, a) => sum + (a.buttonWidth ?? 52), 0);
    cols.push({
      headerName: '',
      colId: '__row_actions_right',
      width: actionsWidth,
      minWidth: actionsWidth,
      maxWidth: actionsWidth,
      sortable: false,
      filter: false,
      resizable: false,
      pinned: 'right',
      suppressMenu: true,
      cellRenderer: ({ data }: { data?: Record<string, any> }) => (
        data ? <GridRowActions row={data} actions={rightRowActions} /> : null
      ),
    });
  }

  return cols;
}

function missingRequiredParams(report: ProcessedReportInfo, values: Record<string, string>): string[] {
  return report.parameters
    .filter((p) => p.is_required && !values[p.url_key])
    .map((p) => p.ui_label ?? p.name);
}

export function ReportGrid({
  report,
  metadata,
  navigateToRecord,
  onRowClick,
  prefetchRowRecord,
  rowActions = EMPTY_REPORT_ROW_ACTIONS,
  showHeader = true,
  showDescription = true,
  showParameters = true,
  className,
  height,
  fillHeight = false,
  rowHeight = 34,
  headerHeight = 32,
  fitColumns = false,
  showViewRecord = true,
  initialParamValues,
  baseFilterModel,
  onParamValuesApply,
  onEditReport,
  contextBadge,
  headerActions,
  urlState,
  resetSortModel = EMPTY_REPORT_SORT_MODEL,
  initialColumnState: rawInitialColumnState,
  initialColumnDefaults,
  onColumnStateChange,
  countMode = 'exact',
  dashboardKey,
  datasetKey,
  summary,
  rowSelection,
  onSelectionChanged,
  selectionResetKey = 0,
  searchFilterModel,
  searchPlaceholder = 'Search...',
  noRowsMessage,
  onCellEditSaved,
  undo,
}: ReportGridProps): React.ReactElement {
  const fillToViewport = fillHeight && typeof height === 'number';
  const fill = useFillGridHeight(typeof height === 'number' ? height : 0, fillToViewport);
  // Per-table zoom, remembered across sessions per report (REG-653).
  const [tableZoom, setTableZoom] = useTableZoom(`report:${report.report_key}`);
  const { hasCapability } = useMyCapabilities();
  // REG-828: the report's default columns are its report_field order and
  // visibility (Admin > Reports, or "Save as default layout" here). A saved
  // layout records which version of them it was made against, so a column an
  // admin shows later is not silently missing for people with a layout.
  const currentDefaults = useMemo<GridDefaultsSnapshot>(() => ({
    version: Math.max(0, ...report.fields.map((field) => Date.parse(field.updated_at) || 0)),
    visible: report.fields
      .filter((field) => field.is_visible)
      .sort((a, b) => a.ordinal_position - b.ordinal_position)
      .map((field) => field.name),
  }), [report.fields]);
  const initialColumnState = useMemo(() => (
    rawInitialColumnState && rawInitialColumnState.length > 0
      ? reconcileSavedLayout(rawInitialColumnState, initialColumnDefaults, currentDefaults, currentDefaults.visible)
      : rawInitialColumnState
  ), [currentDefaults, initialColumnDefaults, rawInitialColumnState]);
  const popupParent = useMemo<HTMLElement | undefined>(
    () => (typeof document === 'undefined' ? undefined : document.body),
    [],
  );
  const gridRef = useRef<AgGridReact>(null);
  // Close the column chooser / menus with the grid (they live on document.body).
  const onGridPreDestroyed = useGridPopupCleanup(() => gridRef.current?.api);
  // An edit saved outside the grid (merchant side panel) re-fetches the rows
  // in place, without purging what is on screen.
  useEffect(() => onReportGridRefresh(() => {
    gridRef.current?.api?.refreshServerSide({ purge: false });
  }), []);
  // REG-670: related columns currently on screen. Hidden ones are left out of
  // the request so their join stays pruned.
  const visibleRefColumnsRef = useRef<string[]>([]);
  const applyingStateRef = useRef<boolean>(false);
  const [gridReady, setGridReady] = useState(false);
  const activeFilters = useActiveColumnFilters();
  const defaultFilterModel = urlState?.filterModel ?? EMPTY_REPORT_FILTER_MODEL;
  const defaultSortModel = urlState?.sortModel ?? EMPTY_REPORT_SORT_MODEL;
  const paramValues = useMemo(() => paramValuesForReport(report, initialParamValues), [report, initialParamValues]);
  const [pendingParams, setPendingParams] = useState<Record<string, string>>(paramValues);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editMode, setEditMode] = useState(false);
  const { schedule: scheduleRowPrefetch, cancel: cancelRowPrefetch } = useHoverPrefetch();
  const baseTableSchema = report.primary_table_name
    ? metadata.tableData?.byName?.[report.primary_table_name]?.schema_name ?? 'public'
    : 'public';
  const handleLocksChanged = useCallback(() => {
    gridRef.current?.api?.refreshCells({ force: true, suppressFlash: true });
  }, []);
  const {
    getLockOwner,
    acquire: acquireEditLock,
    release: releaseEditLock,
    releaseAll: releaseAllEditLocks,
  } = useEditLocks({
    schema: baseTableSchema,
    tableName: report.primary_table_name ?? '',
    onChange: handleLocksChanged,
    enabled: Boolean(report.primary_table_name),
  });
  const hasFullTextSearch = Boolean(searchFilterModel) || report.fields.some((field) => field.name === 'full_text_search');
  const hasEditableFields = report.fields.some((field) => {
    if (!field.is_visible) return false;
    const uiData = typeof field.ui_data === 'object' && field.ui_data !== null ? field.ui_data : {};
    const editField = typeof uiData.edit_field === 'string' ? uiData.edit_field : field.name;
    const baseField = report.primary_table_name
      ? metadata.fieldData?.[report.primary_table_name]?.byFieldName?.[editField]
      : null;
    return isReportColumnEditable(field, baseField, hasCapability)
      && Boolean(reportCellEditorConfig(baseField, metadata));
  });

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(searchInput.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!editMode) releaseAllEditLocks();
  }, [editMode, releaseAllEditLocks]);

  useEffect(() => {
    setPendingParams(paramValues);
  }, [paramValues]);

  const handleParamChange = useCallback((k: string, v: string) => {
    setPendingParams((prev) => ({ ...prev, [k]: v }));
  }, []);

  const handleApply = useCallback(() => {
    onParamValuesApply?.(pendingParams);
    gridRef.current?.api?.refreshServerSide({ purge: true });
  }, [onParamValuesApply, pendingParams]);

  // Unindexed columns sort only while few rows match (UNINDEXED_SORT_MAX_ROWS):
  // their headers stop sorting once a count says more match, and a sort that
  // arrives first (a shared link, a widened filter) is dropped by the data
  // source with a note.
  const limitSorts = hasIndexedSorts(report);
  const [manyRows, setManyRows] = useState(false);
  const colDefs = useMemo<ColDef[]>(() => {
    const built = buildColDefs(report, metadata, pendingParams, navigateToRecord, showViewRecord, rowActions, editMode, getLockOwner, hasCapability);
    const cols = !limitSorts || !manyRows ? built : built.map((col) => (
      col.field && !isIndexedSort(col.field, report.fields)
        ? { ...col, sortable: false, headerTooltip: `Filter to ${UNINDEXED_SORT_MAX_ROWS.toLocaleString()} rows or fewer to sort by ${col.headerName ?? col.field}` }
        : col));
    // A column the page sorts descending by default (Changed, newest first):
    // clearing its sort falls back to that same default, so the usual
    // asc -> desc -> none cycle could never reach ascending from a header
    // click. Its cycle starts at descending instead: one click gives ascending.
    const defaultDesc = new Set(resetSortModel.filter((item) => item.sort === 'desc').map((item) => item.colId));
    const sorted = defaultDesc.size === 0 ? cols : cols.map((col) => (
      col.field && defaultDesc.has(col.field) ? { ...col, sortingOrder: ['desc', 'asc', null] as ColDef['sortingOrder'] } : col));
    const withSelection = rowSelection ? [BULK_SELECTION_COLUMN, ...sorted] : sorted;
    return summary ? withSummaryRowSupport(withSelection) : withSelection;
  }, [editMode, manyRows, getLockOwner, hasCapability, limitSorts, metadata, navigateToRecord, pendingParams, report, resetSortModel, rowActions, rowSelection, showViewRecord, summary]);
  const onManyRows = useCallback((many: boolean) => setManyRows(many), []);
  // The server-side row model shows no "no rows" overlay of its own.
  const [hasRows, setHasRows] = useState(true);
  const onSortDropped = useCallback((colIds: string[]) => {
    const api = gridRef.current?.api;
    api?.applyColumnState({ state: colIds.map((colId) => ({ colId, sort: null })) });
    const names = colIds.map((id) => api?.getColumn(id)?.getColDef().headerName ?? id).join(', ');
    message.info(`Sorting by ${names} needs ${UNINDEXED_SORT_MAX_ROWS.toLocaleString()} rows or fewer. Filter first, then sort.`);
  }, []);

  const effectiveBaseFilterModel = useMemo(() => {
    let searchModel: Record<string, any> | null = null;
    if (searchQuery && searchFilterModel) searchModel = searchFilterModel(searchQuery);
    else if (searchQuery && hasFullTextSearch) {
      searchModel = {
        full_text_search: {
          filterType: 'text',
          type: 'plain_fts',
          filter: searchQuery,
        },
      };
    }
    return { ...(baseFilterModel ?? {}), ...(searchModel ?? {}) };
  }, [baseFilterModel, hasFullTextSearch, searchFilterModel, searchQuery]);

  useEffect(() => {
    if (selectionResetKey) gridRef.current?.api?.deselectAll();
  }, [selectionResetKey]);
  const handleSelectionChanged = useCallback((event: any) => {
    onSelectionChanged?.(event.api.getSelectedRows());
  }, [onSelectionChanged]);
  const getSelectableRowId = useCallback((params: any) => String(params.data?.id), []);

  // REG-885: reports whose grid the search API serves (Lead Explorer).
  const searchGrid = useMemo(
    () => (report.source_kind === 'view' ? searchGridFetcher(report.report_key) : null),
    [report.report_key, report.source_kind],
  );
  const datasource = useMemo<IServerSideDatasource>(() => {
    return createReportDataSource({
      report,
      paramValues: pendingParams,
      baseFilterModel: effectiveBaseFilterModel,
      baseSortModel: defaultSortModel,
      countMode,
      telemetryContext: dashboardKey && datasetKey ? { dashboardKey, datasetKey } : undefined,
      visibleRefColumnsRef,
      onManyRows,
      onSortDropped,
      onFirstPage: noRowsMessage ? setHasRows : undefined,
      searchGrid,
    });
  }, [countMode, dashboardKey, datasetKey, defaultSortModel, effectiveBaseFilterModel, report, pendingParams, onManyRows, onSortDropped, noRowsMessage, searchGrid]);

  // Filtered-totals pinned row (REG-650). Grid-level filters live inside AG
  // Grid, so mirror them in state to key the aggregate fetch.
  const [liveFilterModel, setLiveFilterModel] = useState<Record<string, any>>(defaultFilterModel);
  const summaryFiltersActive = Boolean(summary)
    && (Object.keys(liveFilterModel).length > 0 || Object.keys(effectiveBaseFilterModel).length > 0);
  const summaryQuery = summary ? reportSummaryQueryOptions({
      summary,
      report,
      paramValues: pendingParams,
      baseFilterModel: effectiveBaseFilterModel,
      filterModel: liveFilterModel,
    }) : {
      queryKey: [REPORT_ROWS_QUERY_KEY, 'summary-row', 'disabled'],
      queryFn: async () => ({}),
    };
  const { data: summaryRow } = useQuery({
    ...summaryQuery,
    queryKey: summaryQuery.queryKey as readonly unknown[],
    enabled: summaryFiltersActive,
  });
  const pinnedTopRowData = useMemo(
    () => (summaryFiltersActive && summaryRow ? [summaryRow] : undefined),
    [summaryFiltersActive, summaryRow],
  );

  const onGridReady = useCallback((event: GridReadyEvent) => {
    applyingStateRef.current = true;
    applySavedColumnState(event.api, initialColumnState);
    event.api.setFilterModel(defaultFilterModel);
    applySortModel(event.api, defaultSortModel);
    if (fitColumns) event.api.sizeColumnsToFit();
    applyingStateRef.current = false;
    setGridReady(true);
    setLiveFilterModel(event.api.getFilterModel());
    activeFilters.attach(event.api);
  }, [activeFilters.attach, defaultFilterModel, defaultSortModel, fitColumns, initialColumnState]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    const currentFilterModel = api.getFilterModel();
    const currentSortModel = sortModelFromGridApi(api);
    const nextFilterJson = stableJson(defaultFilterModel);
    const nextSortJson = stableJson(defaultSortModel);

    const filterChanged = stableJson(currentFilterModel) !== nextFilterJson;
    const sortChanged = stableJson(currentSortModel) !== nextSortJson;

    applyingStateRef.current = true;
    applySavedColumnState(api, initialColumnState);
    if (filterChanged) {
      api.setFilterModel(defaultFilterModel);
    }
    if (sortChanged) {
      applySortModel(api, defaultSortModel);
    }
    if (filterChanged || sortChanged) {
      api.refreshServerSide({ purge: true });
    }
    applyingStateRef.current = false;
    if (filterChanged) setLiveFilterModel(api.getFilterModel());
  }, [defaultFilterModel, defaultSortModel, initialColumnState]);

  const handleFilterChanged = useCallback((event: any) => {
    setLiveFilterModel(event.api.getFilterModel());
    if (applyingStateRef.current) return;
    urlState?.onFilterModelChange?.(event.api.getFilterModel());
  }, [urlState]);

  // REG-802: the column menu ends with that column's total, average or most
  // common value over every row the current filters match.
  const getMainMenuItems = useCallback((params: GetMainMenuItemsParams) => {
    const colId = params.column.getColId();
    const plan = reportColumnStatPlan(report, colId);
    if (!plan) return params.defaultItems;
    const filterModel = { ...effectiveBaseFilterModel, ...params.api.getFilterModel() };
    return columnMenuWithStat(params, {
      queryKey: reportColumnStatQueryKey(report, pendingParams, filterModel, colId),
      load: () => fetchReportColumnStat({ report, paramValues: pendingParams, filterModel, plan }),
    });
  }, [effectiveBaseFilterModel, pendingParams, report]);

  // Only a sort the user made goes to the URL. The grid also reports the sort
  // it applies itself (the report's default, a moment after it mounts); writing
  // that pushed a history entry, and when it landed after the user had already
  // moved on it navigated them back to this page (setSearchParams resolves
  // against the page it was created on).
  const handleSortChanged = useCallback((event: any) => {
    if (applyingStateRef.current || !isUserColumnEvent(event)) return;
    urlState?.onSortModelChange?.(sortModelFromGridApi(event.api));
  }, [urlState]);

  const handleColumnStateChanged = useCallback((event: any) => {
    if (applyingStateRef.current) return;
    if (event.type === 'columnResized' && event.finished !== true) return;
    // REG-828: only a change the user made is saved as their layout.
    if (isUserColumnEvent(event)) onColumnStateChange?.(cleanedColumnState(event.api), currentDefaults);

    // REG-670: a revealed related column was not in the request, so it needs a
    // refetch or it renders empty.
    if (event.type === 'columnVisible') {
      const next: string[] = event.api
        .getAllDisplayedColumns()
        .map((column: any) => column.getColId())
        .filter((colId: string) => colId.endsWith('_ref'))
        .sort();
      const previous = visibleRefColumnsRef.current;
      const unchanged = next.length === previous.length
        && next.every((colId, index) => colId === previous[index]);
      if (!unchanged) {
        visibleRefColumnsRef.current = next;
        event.api.refreshServerSide({ purge: true });
      }
    }
  }, [currentDefaults, onColumnStateChange]);

  // REG-828: make the columns on screen the report's default (report_field
  // order and visibility, through the same RPC as Admin > Reports, which checks
  // manage_default_layouts or manage_reports). Widths are not part of a
  // report's columns. The saver's own layout is cleared so they
  // follow the default like everyone else.
  const handleSaveDefaultLayout = useCallback(async () => {
    const api = gridRef.current?.api;
    if (!api) return;
    const reportFields = new Set(report.fields.map((field) => field.name));
    const fields = api.getColumnState()
      .filter((column) => reportFields.has(column.colId))
      .map((column) => ({ name: column.colId, visible: !column.hide }));
    const { error } = await (supabaseClient as any).rpc('save_report_field_order', {
      p_report_key: report.report_key,
      p_fields: fields,
    });
    if (error) throw error;
    onColumnStateChange?.([]);
    await invalidateAndRefreshAllQueriesForTable('report_field');
  }, [onColumnStateChange, report.fields, report.report_key]);

  // A materialized report's background refresh finished: show its new numbers.
  const refreshMaterializedRows = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [REPORT_ROWS_QUERY_KEY] });
    gridRef.current?.api?.refreshServerSide({ purge: true });
  }, []);

  const handleResetColumns = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    applyingStateRef.current = true;
    resetColumnLayout(api);
    applySortModel(api, resetSortModel);
    if (fitColumns) api.sizeColumnsToFit();
    applyingStateRef.current = false;

    onColumnStateChange?.([]);
    urlState?.onSortModelChange?.([]);
  }, [fitColumns, onColumnStateChange, resetSortModel, urlState]);

  const handleResetFilters = useCallback(() => {
    const api = gridRef.current?.api;
    if (api) resetGridFilters(api);
  }, []);

  const missing = report.source_kind === 'function' ? missingRequiredParams(report, pendingParams) : [];
  const handleRowClicked = useCallback((event: RowClickedEvent<Record<string, any>>) => {
    if (event.node?.rowPinned) return;
    if (editMode || !event.data || isGridInteractiveTarget(event.event?.target ?? null)) return;
    onRowClick?.(event.data);
  }, [editMode, onRowClick]);
  const handleCellMouseOver = useCallback((event: CellMouseOverEvent<Record<string, any>>) => {
    if (editMode || !event.data || !prefetchRowRecord) return;
    scheduleRowPrefetch(() => prefetchRowRecord(event.data!));
  }, [editMode, prefetchRowRecord, scheduleRowPrefetch]);
  const handleCellValueChanged = useCallback(async (event: CellValueChangedEvent<Record<string, any>>) => {
    if (!report.primary_table_name || !event.data?.id || event.newValue === event.oldValue) return;
    const columnContext = event.colDef.context as { editField?: string; dataType?: string } | undefined;
    const editField = String(columnContext?.editField ?? event.colDef.field ?? '');
    if (!editField) return;
    try {
      const result = await updateRecord({
        tableName: report.primary_table_name,
        schema: baseTableSchema,
        data: { [editField]: event.newValue },
        whereClause: { id: event.data.id },
        options: {
          invalidateCache: false,
          fieldName: editField,
          expectedValues: { [editField]: event.oldValue ?? null },
          expectedValueTypes: { [editField]: columnContext?.dataType },
        },
      });
      if (result.error) throw result.error;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [REPORT_ROWS_QUERY_KEY] }),
        invalidateAndRefreshAllQueriesForTable(report.primary_table_name),
      ]);
      event.api.refreshServerSide({ purge: true });
      message.success('Saved');
      onCellEditSaved?.({
        tableName: report.primary_table_name,
        schema: baseTableSchema,
        recordId: event.data.id,
        field: editField,
        oldValue: event.oldValue ?? null,
        newValue: event.newValue ?? null,
        label: event.colDef.headerName || editField,
        dataType: columnContext?.dataType,
      });
    } catch (error) {
      console.error(`Error updating ${report.primary_table_name}:`, error);
      message.error(error instanceof Error ? error.message : 'Could not save this change');
      event.api.undoCellEditing();
    }
  }, [baseTableSchema, onCellEditSaved, report.primary_table_name]);
  const handleCellEditingStarted = useCallback((event: CellEditingStartedEvent<Record<string, any>>) => {
    if (event.data?.id != null) acquireEditLock(event.data.id, event.column.getColId());
  }, [acquireEditLock]);
  const handleCellEditingStopped = useCallback((event: CellEditingStoppedEvent<Record<string, any>>) => {
    if (event.data?.id != null) releaseEditLock(event.data.id, event.column.getColId());
  }, [releaseEditLock]);

  const getExportConfiguration = (): ExportConfiguration => {
    const api = gridRef.current!.api;
    const reportFieldNames = new Set(report.fields.map((field) => field.name));
    const columns = gridExportColumns(api, (key) => key !== 'full_text_search' && reportFieldNames.has(key),
      metadata?.routeData?.tableToRouteName);
    const filterModel = { ...effectiveBaseFilterModel, ...api.getFilterModel() };
    const hasId = report.fields.some((field) => field.name === 'id');
    const gridSort = sortModelFromGridApi(api);
    const stableSort = hasId ? [{ colId: 'id', sort: 'asc' as const }] : report.fields
      .filter((field) => !/json|tsvector|array/i.test(field.data_type))
      .map((field) => ({ colId: field.name, sort: 'asc' as const }));
    return {
      name: report.ui_label || report.report_key,
      columns, selectedRows: hasId ? api.getSelectedRows() : undefined, rowKey: hasId ? 'id' : undefined,
      // The grid's sort, else the page's default, else the report's (as the data source).
      order: serverGridOrder(reportOrderTerms(report.fields, gridSort.length > 0 ? gridSort : defaultSortModel)
        .map(({ column, ascending }) => ({ field: column, desc: !ascending })), loadedGridRows(api)),
      filterSummary: [describeExportFilters(filterModel, Object.fromEntries(report.fields.map((field) => [field.name, field.ui_label || field.name]))), ...report.parameters.filter((parameter) => pendingParams[parameter.url_key]).map((parameter) => `${parameter.ui_label || parameter.name}: ${pendingParams[parameter.url_key]}`)].join('; '),
      load: async ({ offset, after, limit, signal, fields, selectedIds }) => {
        const start = hasId ? 0 : offset;
        const result = await fetchReportRowsFromSupabase({
          report, paramValues: { ...pendingParams }, filterModel,
          sortModel: stableSort,
          startRow: start, endRow: start + limit, afterId: hasId ? after : null,
          // large reports export without a total (see createReportDataSource)
          countMode: offset === 0 && !limitSorts ? 'exact' : 'none', signal, selectedIds,
          fields: [...new Set([...fields, ...(hasId ? ['id'] : [])])],
        });
        return { rows: result.rows, total: offset === 0 ? result.totalCount ?? null : null };
      },
    };
  };
  const exportButton = <ExportButton disabled={!gridReady || missing.length > 0} getConfiguration={getExportConfiguration} />;

  return (
    <div
      className={className}
      ref={fillToViewport ? fill.ref : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: fillToViewport ? fill.height : height ?? '100%',
        minHeight: 0,
        // The theme's sizing vars are calc()-scaled by this factor (REG-653).
        ['--sky-grid-zoom' as string]: tableZoom,
      }}
    >
      {showHeader && (
        <div className="sky-report-header">
          <div>
            <div className="sky-report-title-row">
              <h3>{report.ui_label ?? report.report_key}</h3>
              {contextBadge && (
                <span className="sky-report-context-badge">
                  <i aria-hidden="true" />
                  {contextBadge}
                </span>
              )}
            </div>
            {showDescription && report.ui_description && <p>{report.ui_description}</p>}
            {report.source_kind === 'materialized_view' && (
              <MaterializedReportStatus name={reportMaterializedName(report.report_key)} onRefreshed={refreshMaterializedRows} />
            )}
          </div>
          <div className="sky-report-header-actions">
            <ResetColumnsButton disabled={!gridReady} onReset={handleResetColumns} />
            <SaveDefaultLayoutButton disabled={!gridReady} onSave={handleSaveDefaultLayout} />
            <ResetFiltersButton disabled={!gridReady} activeCount={activeFilters.filters.length} onReset={handleResetFilters} />
            <GridZoomControl zoom={tableZoom} onChange={setTableZoom} />
            {exportButton}
            {headerActions}
            {hasFullTextSearch && (
              <InputGroup
                small
                leftIcon="search"
                type="search"
                aria-label={`Search ${report.ui_label ?? 'report'}`}
                placeholder={searchPlaceholder}
                value={searchInput}
                onChange={(event) => setSearchInput(event.currentTarget.value)}
                rightElement={searchInput ? (
                  <Button minimal small icon="cross" aria-label="Clear search" onClick={() => setSearchInput('')} />
                ) : undefined}
              />
            )}
            {hasEditableFields && undo && (
              <UndoLastEditButton canUndo={undo.canUndo} nextUndoLabel={undo.nextUndoLabel} onClick={undo.onUndo} />
            )}
            {hasEditableFields && (
              <EditModeToggle
                active={editMode}
                onClick={() => setEditMode((current) => !current)}
              />
            )}
            {onEditReport && (
              <Button small minimal icon="cog" aria-label="Configure report" onClick={onEditReport} />
            )}
          </div>
        </div>
      )}

      {/* Dashboard report grids hide the report header (the dashboard component
          shell already supplies a title), so the zoom stepper — and the edit
          toggle when the grid is editable — get their own strip. */}
      {!showHeader && (
        <div className="sky-report-inline-actions">
          {exportButton}
          <ResetColumnsButton disabled={!gridReady} onReset={handleResetColumns} />
          <SaveDefaultLayoutButton disabled={!gridReady} onSave={handleSaveDefaultLayout} />
          <ResetFiltersButton disabled={!gridReady} activeCount={activeFilters.filters.length} onReset={handleResetFilters} />
          <GridZoomControl zoom={tableZoom} onChange={setTableZoom} />
          {hasEditableFields && undo && (
            <UndoLastEditButton canUndo={undo.canUndo} nextUndoLabel={undo.nextUndoLabel} onClick={undo.onUndo} />
          )}
          {hasEditableFields && (
            <EditModeToggle
              active={editMode}
              onClick={() => setEditMode((current) => !current)}
            />
          )}
        </div>
      )}

      {showParameters && report.source_kind === 'function' && report.parameters.length > 0 && (
        <ReportParameterBar
          parameters={report.parameters}
          values={pendingParams}
          onChange={handleParamChange}
          onApply={handleApply}
        />
      )}

      <ActiveFilterChips filters={activeFilters.filters} onRemove={activeFilters.removeFilter} />

      {missing.length > 0 ? (
        <NonIdealState
          icon="form"
          title="Parameters required"
          description={`Fill in required parameters: ${missing.join(', ')}`}
        />
      ) : (
        <div className="ag-theme-alpine" style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {noRowsMessage && !hasRows ? (
            <div className="report-grid-empty" role="status">{noRowsMessage}</div>
          ) : null}
          <AgGridReact
            ref={gridRef}
            columnDefs={colDefs}
            rowModelType="serverSide"
            // REG-828: bulk selection keys rows by id so a refetch keeps them ticked.
            rowSelection={rowSelection}
            getRowId={rowSelection ? getSelectableRowId : undefined}
            suppressRowClickSelection={Boolean(rowSelection)}
            onSelectionChanged={rowSelection ? handleSelectionChanged : undefined}
            serverSideDatasource={datasource}
            pinnedTopRowData={pinnedTopRowData}
            // Server-side datasource changes already replace the row store. Disabling
            // row animation keeps that legitimate reload continuously opaque.
            animateRows={false}
            onGridReady={onGridReady}
            onGridPreDestroyed={onGridPreDestroyed}
            onRowClicked={onRowClick ? handleRowClicked : undefined}
            onCellMouseOver={prefetchRowRecord ? handleCellMouseOver : undefined}
            onCellMouseOut={cancelRowPrefetch}
            onCellValueChanged={handleCellValueChanged}
            onCellEditingStarted={handleCellEditingStarted}
            onCellEditingStopped={handleCellEditingStopped}
            singleClickEdit={editMode}
            stopEditingWhenCellsLoseFocus
            undoRedoCellEditing
            // Dashboard grids are only ~620px tall, so a dropdown opened on one
            // of the last rows would be clipped by the grid viewport. Anchoring
            // popups to the document lets the list escape it.
            popupParent={popupParent}
            suppressCsvExport={true}
            suppressExcelExport={true}
            onFilterChanged={handleFilterChanged}
            getMainMenuItems={getMainMenuItems}
            onSortChanged={handleSortChanged}
            // REG-968: colDefs is rebuilt on re-renders. Without this AG Grid
            // puts the columns back in colDefs order every time, which undid
            // a column drag at each step. Reset columns still restores the
            // colDefs order (resetColumnState).
            maintainColumnOrder
            onColumnMoved={handleColumnStateChanged}
            onColumnPinned={handleColumnStateChanged}
            onColumnResized={handleColumnStateChanged}
            onColumnVisible={handleColumnStateChanged}
            pagination
            paginationPageSize={REPORT_GRID_PAGE_SIZE}
            rowHeight={Math.round(rowHeight * tableZoom)}
            headerHeight={Math.round(headerHeight * tableZoom)}
            onGridSizeChanged={(event) => { if (fitColumns) event.api.sizeColumnsToFit(); }}
            onFirstDataRendered={(event) => { if (fitColumns) event.api.sizeColumnsToFit(); }}
            defaultColDef={{ resizable: true, sortable: true, filter: true }}
          />
        </div>
      )}
    </div>
  );
}
